import { useState, type FC } from 'react'

import { Checkbox, Textarea } from '@skytech/ui'
import type { FieldProps } from 'formik'
import { Field, useFormikContext } from 'formik'

import { Translation } from '@/app/translations/Translation'
import Tabs from '@/components/Tabs'
import { useIsAdmin } from '@/hooks/access'
import type { PolicyExtended } from '@/modules/ecom-policy/types'

import type { FormFields } from '../types'

import { AdminOverwriteUserId } from './AdminOverwriteUserId'

import { usePolicyFilters } from '../../Policy.context'

export const AdvancedSettings: FC<{ policy: PolicyExtended; isDisabled?: boolean }> = ({
  policy,
  isDisabled = false,
}) => {
  const [selectedTab, setSelectedTab] = useState('general')
  const tabs = [
    { value: 'general', label: <Translation id="General" /> },
    { value: 'messages', label: <Translation id="Messages" /> },
  ]

  return (
    <>
      <h2>
        <Translation id="Advanced settings" />
      </h2>

      <Tabs
        selectedValue={selectedTab}
        tabs={tabs}
        onClick={tabId => {
          setSelectedTab(String(tabId))
        }}
      />
      {selectedTab === 'general' && <GeneralTab policy={policy} isDisabled={isDisabled} />}
      {selectedTab === 'messages' && <OtherTab isDisabled={isDisabled} />}
    </>
  )
}

const GeneralTab: FC<{ isDisabled: boolean; policy: PolicyExtended }> = ({
  isDisabled,
  policy,
}) => {
  const [, setIsVatIncluded] = usePolicyFilters().isVatIncluded
  const isAdmin = useIsAdmin()
  const { values, setFieldValue, initialValues } = useFormikContext<FormFields>()
  return (
    <>
      <AdminOverwriteUserId isDisabled={isDisabled} />

      {policy?.isGrossSalarySchemeAvailableForPolicy && (
        <Field name="isGrossSalaryScheme">
          {({ field }: FieldProps) => (
            <Checkbox
              capitalizeLabel
              id="isGrossSalaryScheme"
              label={<Translation id="Use gross salary scheme" />}
              marginTop={16}
              isDisabled={isDisabled}
              checked={field.value}
              {...field}
              onChange={e => {
                field.onChange(e)
                if (e.target.checked) {
                  setFieldValue('paymentInterval', values.coverInterval)
                  setFieldValue('minimumAmount', 0)
                } else {
                  setFieldValue('paymentInterval', initialValues.paymentInterval)
                  setFieldValue('minimumAmount', initialValues.minimumAmount)
                }
              }}
            />
          )}
        </Field>
      )}

      <Field name="hasVat">
        {({ field }: FieldProps) => (
          <Checkbox
            capitalizeLabel
            id="hasVat"
            label={<Translation id="Include VAT in price" />}
            marginTop={16}
            isDisabled={isDisabled}
            checked={field.value}
            onFieldChange={() => setIsVatIncluded(!field.value)}
            {...field}
          />
        )}
      </Field>

      {isAdmin && (
        <Field name="isAdminRestricted">
          {({ field, form }: FieldProps) => (
            <Checkbox
              capitalizeLabel
              id="isAdminRestricted"
              label={<Translation id="Is admin restricted" />}
              checked={field.value ?? false}
              isDisabled={isDisabled}
              {...field}
              onChange={e => {
                field.onChange(e)
                form.setFieldValue('isManagerAccessible', false)
              }}
            />
          )}
        </Field>
      )}

      <Field name="isManagerAccessible">
        {({ field, form }: FieldProps) => (
          <Checkbox
            capitalizeLabel
            id="isManagerAccessible"
            label={<Translation id="Is manager accessible" />}
            checked={field.value}
            isDisabled={isDisabled || form.values.isAdminRestricted}
            {...field}
          />
        )}
      </Field>

      {/*
        Temporary: keep the isRelationsIgnored field implementation in place, but hide the
        checkbox from the Advanced Settings modal until we decide whether to remove it fully.
      */}
      {/* <Field name="isRelationsIgnored">
        {({ field }: FieldProps) => (
          <Checkbox
            capitalizeLabel
            id="isRelationsIgnored"
            label={<Translation id="Ignore relations" />}
            checked={field.value}
            isDisabled={isDisabled}
            {...field}
          />
        )}
      </Field> */}

      <Field name="isProductViewAllowedWhenLocked">
        {({ field }: FieldProps) => (
          <Checkbox
            capitalizeLabel
            id="isProductViewAllowedWhenLocked"
            label={<Translation id="Allow employees to view products while policy is locked" />}
            checked={field.value}
            isDisabled={isDisabled}
            {...field}
          />
        )}
      </Field>

      <Field name="isProductPriceVisible">
        {({ field }: FieldProps) => (
          <Checkbox
            capitalizeLabel
            id="isProductPriceVisible"
            label={<Translation id="Show full product price" />}
            checked={field.value}
            isDisabled={isDisabled}
            marginBottom={16}
            {...field}
          />
        )}
      </Field>
    </>
  )
}

const OtherTab: FC<{ isDisabled: boolean }> = ({ isDisabled }) => (
  <>
    <Field name="messageToEmployee">
      {({ field, meta }: FieldProps) => (
        <Textarea
          label={<Translation id="Message to employee" />}
          isFullWidth
          id="messageToEmployee"
          isDisabled={isDisabled}
          error={meta.error}
          touched={meta.touched}
          {...field}
        />
      )}
    </Field>

    <Field name="stepMainDescription">
      {({ field, meta }: FieldProps) => (
        <Textarea
          marginTop={16}
          label={<Translation id="Step main description" />}
          isFullWidth
          id="stepMainDescription"
          isDisabled={isDisabled}
          error={meta.error}
          touched={meta.touched}
          {...field}
        />
      )}
    </Field>

    <Field name="stepAccessoriesDescription">
      {({ field, meta }: FieldProps) => (
        <Textarea
          marginTop={16}
          marginBottom={16}
          label={<Translation id="Step accessories description" />}
          isFullWidth
          id="stepAccessoriesDescription"
          isDisabled={isDisabled}
          error={meta.error}
          touched={meta.touched}
          {...field}
        />
      )}
    </Field>
  </>
)
